import React, { useMemo, useState } from 'react';
import { Lock } from 'lucide-react';
import api from '../../services/api';
import AppLayout from '../../components/AppLayout';
import PageHeader from '../../components/PageHeader';
import DataTable from '../../components/DataTable';
import useTableList from '../../utils/useTableList';
import ConfirmDialog from '../../components/ConfirmDialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import Drawer from '../../components/Drawer';
import useEditRoute from '../../utils/useEditRoute';
import { MODULES, MODULE_OPTIONS } from '../../constants/listOptions';
import usePageTitle from '../../utils/usePageTitle';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';

function RolesSettings() {
    usePageTitle('Perfis');
    const { toast, toastApiError } = useToast();
    const { can } = useAuth();
    const canRoles = can('Roles');

    const sortedModules = useMemo(() => {
        return [...MODULES].sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
    }, []);

    const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editingRole, setEditingRole] = useState(null);
    const [formLoading, setFormLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        permissions: {},
    });

    const list = useTableList({
        storageKey: 'roles',
        fetcher: (params) => api.get('/roles', { params }),
        errorMessage: 'Não foi possível carregar perfis',
    });

    const openNew = () => {
        setEditingRole(null);
        setFormData({
            name: '',
            permissions: {},
        });
        setDrawerOpen(true);
    };

    const openEdit = (role) => {
        setEditingRole(role);
        const perms = {};
        (role.permissions || []).forEach((p) => {
            perms[p.module] = !!p.allowed;
        });
        setFormData({
            name: role.name || '',
            permissions: perms,
        });
        setDrawerOpen(true);
    };

    const togglePermission = (moduleKey) => {
        setFormData((prev) => ({
            ...prev,
            permissions: {
                ...prev.permissions,
                [moduleKey]: !prev.permissions[moduleKey],
            },
        }));
    };

    const normalizedPermissions = useMemo(() => {
        return MODULES.map((m) => ({
            module: m.key,
            allowed: !!formData.permissions[m.key],
        }));
    }, [formData.permissions]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormLoading(true);
        try {
            const payload = {
                name: formData.name,
                permissions: normalizedPermissions,
            };

            if (editingRole) {
                await api.put(`/roles/${editingRole.id}`, payload);
            } else {
                await api.post('/roles', payload);
            }

            list.reload();
            toast({
                title: editingRole ? 'Perfil salvo' : 'Perfil criado',
                description: editingRole ? 'Alterações salvas com sucesso' : 'Cadastro realizado com sucesso',
                variant: 'success',
            });
        } catch (e) {
            console.error('Error saving role:', e);
            toastApiError(e, { title: 'Erro ao salvar', fallback: 'Não foi possível salvar o perfil' });
        } finally {
            setFormLoading(false);
        }
    };

    const handleDeleteClick = (role) => {
        setDeleteDialog({ open: true, id: role.id });
    };

    const handleDeleteConfirm = async () => {
        try {
            await api.delete(`/roles/${deleteDialog.id}`);
            setDeleteDialog({ open: false, id: null });
            list.reload();
            toast({
                title: 'Perfil excluído',
                description: 'O perfil foi excluído com sucesso',
                variant: 'success',
            });
        } catch (e) {
            console.error('Error deleting role:', e);
            toastApiError(e, { title: 'Erro ao excluir', fallback: 'Não foi possível excluir o perfil' });
        }
    };

    const columns = [
        {
            key: 'name',
            label: 'Nome',
            className: 'font-medium',
        },
        {
            key: 'permissions',
            label: 'Permissões',
            render: (_, row) => {
                const allowed = (row.permissions || [])
                    .filter((p) => p.allowed)
                    .map((p) => p.module);

                if (allowed.length === 0) return '-';

                const labels = allowed
                    .map((k) => MODULES.find((m) => m.key === k)?.label || k)
                    .sort((a, b) => a.localeCompare(b, 'pt-BR'))
                    .join(', ');

                return <span className="text-sm text-gray-700">{labels}</span>;
            },
        },
    ];

    const filters = [
        { key: 'name', label: 'Nome', type: 'text' },
        { key: 'module', label: 'Módulo com acesso', type: 'select', options: MODULE_OPTIONS },
    ];

    useEditRoute({
        basePath: '/settings/roles',
        ready: list.loaded,
        drawerOpen: drawerOpen,
        open: id => {
            const row = list.rows.find((r) => r.id === id);
            if (row) openEdit(row);
        },
    });

    return (
        <AppLayout>
            <div className="space-y-6">
                <PageHeader
                    title="Perfis"
                    breadcrumbs={[
                        { label: 'Dashboard', path: '/dashboard' },
                        { label: 'Configurações' },
                    ]}
                    actions={[
                        ...(canRoles ? [{
                            label: 'Novo Perfil',
                            icon: 'new',
                            onClick: openNew,
                        }] : []),
                    ]}
                />

                {/* Table */}
                <DataTable
                    list={list}
                    filters={filters}
                    searchPlaceholder="Buscar por nome ou descrição..."
                    columns={columns}
                    editTo={canRoles ? (row) => `/settings/roles/${row.id}` : undefined}
                    onEdit={canRoles ? openEdit : undefined}
                    onDelete={canRoles ? handleDeleteClick : undefined}
                    pagination={false}
                    emptyMessage="Nenhum perfil cadastrado"
                />
            </div>

            <Drawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                title={editingRole ? 'Editar Perfil' : 'Novo Perfil'}
                size="md"
                footer={
                    <div className="flex justify-end gap-3">
                        <Button type="button" variant="outline" onClick={() => setDrawerOpen(false)}>
                            Cancelar
                        </Button>
                        <Button type="submit" form="role-form" disabled={formLoading}>
                            {formLoading ? 'Salvando...' : 'Salvar'}
                        </Button>
                    </div>
                }
            >
                <form id="role-form" onSubmit={handleSubmit} className="space-y-8" autoComplete="off">
                    {/* Seção: Identificação */}
                    <div>
                        <div className="flex items-center gap-2 mb-4">
                            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <h3 className="text-md font-semibold text-gray-900">
                                Identificação
                            </h3>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Nome *
                                </label>
                                <Input
                                    name="name"
                                    value={formData.name}
                                    onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                                    required
                                    placeholder="Nome do perfil"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Seção: Permissões */}
                    <div>
                        <div className="flex items-center gap-2 mb-4">
                            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                                <Lock className="w-4 h-4 text-blue-600" />
                            </div>
                            <h3 className="text-md font-semibold text-gray-900">
                                Permissões por módulo
                            </h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {sortedModules.map((m) => (
                                <label key={m.key} className="flex items-center justify-between gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                                    <div className="text-sm text-gray-700">{m.label}</div>
                                    <input
                                        type="checkbox"
                                        checked={!!formData.permissions[m.key]}
                                        onChange={() => togglePermission(m.key)}
                                        className="h-4 w-4"
                                    />
                                </label>
                            ))}
                        </div>
                    </div>
                </form>
            </Drawer>

            <ConfirmDialog
                open={deleteDialog.open}
                title="Excluir Perfil"
                message="Tem certeza que deseja excluir este perfil? Esta ação não pode ser desfeita."
                onConfirm={handleDeleteConfirm}
                onCancel={() => setDeleteDialog({ open: false, id: null })}
            />
        </AppLayout>
    );
}

export default RolesSettings;

